import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {IntegrationInitiatePaymentResponse, ReferenceType} from "../types/IntegrationPayment";
import {getSelectedLang, getSelectedStore} from "../+state/stores.selectors";
import {Store} from "@ngrx/store";
import {ClientStore, Order, PaymentTransaction} from "../../../stores/stores";
import {takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";
import {
  ErrorMessage,
  LoadCatalogLanguages,
  ViewStateUpdateUserLanguage
} from "../+state/stores.actions";
import {TranslateService} from "@ngx-translate/core";
import {StoreOrderService} from "../../../stores/store-order/store-order.service";
import {
  IntegrationInitiatePaymentSuccess
} from "../../payments/+state/payment.actions";
import {PaymentService} from "../../payments/payment.service";
import {LocationService} from "../../location.service";
import {WhitelabelService} from "../../../shared/whitelabel.service";
import {WINDOW} from "../../window-providers";
import {PaymentsenseService} from "../../payments/paymentsense.service";
import {HideLoader, ShowLoader} from "../../../loader/+state/loader.actions";
import {StoreService} from "../store.service";

@Component({
  selector: 'app-qr-payment-pay',
  templateUrl: './qr-payment-pay.component.html',
  styleUrl: './qr-payment-pay.component.scss'
})
export class QrPaymentPayComponent implements OnInit, OnDestroy {

  storeId: number;
  referenceId: string;
  referenceType: ReferenceType;
  referenceTypeStr: string;
  selectedStore: ClientStore;
  langLoaded = false;
  unsubscribe$: Subject<void> = new Subject<void>();
  lang: string;
  selectedLang: string;
  paymentTransaction: PaymentTransaction;
  requestJSON: IntegrationInitiatePaymentResponse;

  constructor(private route: ActivatedRoute,
              private store: Store<any>,
              private translate: TranslateService,
              private storeOrderService: StoreOrderService,
              private paymentService: PaymentService,
              private locationService: LocationService,
              private whitelabelService: WhitelabelService,
              @Inject(WINDOW) private window: Window,
              private storeService: StoreService,
              ) { }

  ngOnInit(): void {
    this.storeId = +this.route.snapshot.paramMap.get('storeId')!;
    this.referenceId = this.route.snapshot.paramMap.get('referenceId')!;
    this.referenceTypeStr = this.route.snapshot.paramMap.get('referenceType')!;
    this.referenceType = ReferenceType[this.referenceTypeStr];
    this.store.select(getSelectedStore).subscribe((selectedStore) => {
      this.selectedStore = selectedStore;
    });

    this.store.select(getSelectedLang)
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe(value => {
      this.selectedLang = value;
    });

    this.loadLanguage(this.translate.getBrowserLang())
    .catch(_ => {
      this.loadLanguage('en');
      this.langLoaded = true;
      this.store.dispatch(new LoadCatalogLanguages(this.storeId));
    });

    this.loadPaymentTransaction();
  }


  private loadPaymentTransaction() {
    let request$;

    switch (this.referenceType) {
      case ReferenceType.ORDER:
        request$ = this.storeOrderService.get(this.storeId, this.referenceId);
        break;
      case ReferenceType.STANDALONE_PAYMENT:
        request$ = this.storeService.getStandaloneOrderPayment(this.storeId, this.referenceId);
        break;
      default:
        return;
    }

    request$.subscribe({
      next: res => {
        this.store.dispatch(new HideLoader({ payload: {type: 'qr-payment-pay'}}));
        this.paymentTransaction = res?.paymentTransaction
        this.proceedToPayment();
      },
      error: err => console.error('Failed to load payment transaction', err),
    });
  }


  isStandalonePayment() {
    return this.referenceType === ReferenceType.STANDALONE_PAYMENT
  }

  private loadLanguage(locale) {
    return import(`./../../translations/i18n/translation.${locale}.json`)
    .then(lang => {
      this.translate.setTranslation(locale, { ...lang });
      this.translate.setDefaultLang(locale);

      this.langLoaded = true;
      switch (locale) {
        case 'en':
        case 'fr':
        case 'nl':
        case 'el':
        case 'ja':
          this.lang = locale;
          break;
        default:
          this.lang = 'en';
      }

      // store user language to view state...
      this.store.dispatch(new ViewStateUpdateUserLanguage(this.lang, {
        src: 'QrPaymentPayComponent',
        description: 'Load language'
      }));
    });
  }

  proceedToPayment() {
    this.store.dispatch(new ShowLoader({ payload: {type: 'qr-payment-pay'}}));
    const qrPaymentContext = this.paymentTransaction?.paymentParams['QR_PAYMENT_CONTEXT'];
    const initiatePaymentResponse = JSON.parse(qrPaymentContext)?.initiatePaymentResponse;
    this.store.dispatch(new IntegrationInitiatePaymentSuccess(initiatePaymentResponse));
  }

  ngOnDestroy(): void {
    this.store.dispatch(new HideLoader({ payload: {type: 'qr-payment-pay'}}));
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
